import { ArrowUpRightIcon, HelpCircleIcon } from 'lucide-react'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { Modal, TextButton } from 'slab'

import { EmbeddedSupportLinkContent } from '../../EmbeddedSupportLinkContent'
import { useEmployer } from '../../EmployerProvider'

interface SupportOptionProps {
  heading: string
  description: string
  href: string
  onClick?: () => void
}

function SupportOption ({ heading, description, href, onClick }: SupportOptionProps) {
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      onClick={onClick}
      className='flex items-start justify-between gap-4 p-4 border border-gray-200 rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.1)] hover:border-gray-300 hover:bg-gray-50 transition-colors'
    >
      <div>
        <div className='body text-gray-700 mb-1'>{heading}</div>
        <div className='body-sm text-gray-500'>{description}</div>
      </div>
      <ArrowUpRightIcon size={16} className='shrink-0 text-gray-400 mt-1' />
    </a>
  )
}

interface BankingSupportModalProps {
  trigger?: React.ReactNode
}

const SALES_SUPPORT_LINK = 'https://thatch.chilipiper.com/concierge-router/link/in-app-inbound-employer-router'

export function BankingSupportModal ({ trigger }: BankingSupportModalProps) {
  const { isEmbedded } = useEmbeddedOnboarding()
  const { employer } = useEmployer()
  const employerDisplayName = employer?.dba || employer?.name || ''
  const emailSubject = encodeURIComponent(`[Support request] Thatch bank connection for ${employerDisplayName}`)
  const technicalSupportHref = `mailto:support@thatch.com?subject=${emailSubject}`

  const defaultTrigger = (
    <TextButton
      onClick={() => {
        window.segment?.track('[ER] AllowanceTool get help opened', {
          url: window.location.href,
          isEmbedded,
        })
      }}
      leftIcon={HelpCircleIcon}
      color='purple'
    >
      Need help? Get support
    </TextButton>
  )

  return (
    <Modal
      trigger={trigger ?? defaultTrigger}
      width='medium'
      aria-describedby={undefined}
    >
      <Modal.Header>
        <Modal.Title>What do you need help with?</Modal.Title>
      </Modal.Header>
      {isEmbedded
        ? <EmbeddedSupportLinkContent />
        : (
          <Modal.Body>
            <div className='flex flex-col gap-4 sm:flex-row'>
              <SupportOption
                heading='I want more context on how Thatch works'
                description="We'll connect you with our sales team to walk through our platform"
                href={SALES_SUPPORT_LINK}
                onClick={() => {
                  window.segment?.track('[ER] AllowanceTool get support link clicked', {
                    url: window.location.href,
                  })
                }}
              />
              <SupportOption
                heading="Something's not working and I need technical support"
                description="We'll connect you with our support team to troubleshoot"
                href={technicalSupportHref}
                onClick={() => {
                  window.segment?.track('[ER] AllowanceTool email support link clicked', {
                    url: window.location.href,
                  })
                }}
              />
            </div>
          </Modal.Body>
          )}
      {!isEmbedded && (
        <Modal.Footer>
          <div className='flex justify-end'>
            <Modal.Button variant='primary' size='large'>
              Close
            </Modal.Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}
